  import { useEffect, useLayoutEffect, useRef, useState } from "react";
  import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
  import Button from "../../../components/buttons/Button";
  import Image from "../../../components/images/Image";
  import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
  import useApi from "../../../services/useApi";
  import useTranslation from "../../../components/CustomHooks/useTranslation";
  import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
  import config from "../../../config/config";
  import { useNavigate, useParams } from "react-router-dom";
  import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
  import { useDispatch } from "react-redux";
  import { AppDispatch, RootState } from "../../../store";
  import { MasterDistributor, NationalID, PaperTazkera, Passport, ServiceFee, CVM } from "./Components/types";
  import { formatDate } from "../../../components/commonfunctions/convertDate";
  import usePrivilege from "../../../components/CustomHooks/usePrivilege";
  import { showToast } from "../../../components/toastMessage/toastSlice";
  import CustomModal from "../../../components/CustomModal";
  import RoundedLoader from "../../../components/loaders/RoundedLoader";
  import { useSelector } from "react-redux";
  import { toast } from "react-toastify";
  import ToolTip from "../../../components/ToolTip/ToolTip";
  import { setUserUrl } from "../../../store/slices/geolocationSlice";
  import moment from "moment";
  import { resetCVM, setCompleteCVM, setCVMDates, setToDate, setUserId } from "./addMDSlice";
  import { setUserCode, setUserName } from "../../../store/slices/activeListSlice";

  interface UserManagementProps {
    type: string
    testing?: boolean;
    privilege?: string;
  }

  const renderFields = (details: any, fields: { key: string; label: string }[]) => {
    return fields.map(({ key, label }) => {
      const isFileField =
        key === "passport_front_page" ||
        key === "national_id_front_page" ||
        key === "national_id_back_page";
      if (isFileField && details?.[key]) {
        const fileUrl = config?.cdnURL + "public/" + details?.[key];
        const fileName = details?.[key].split("/").pop();
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);
        // const isPDF = /\.pdf$/i.test(fileUrl);

        return (
          <div key={key} className={`${key === "passport_front_page" ? '' : 'mt-2'} relative group`}>
            <h1 className="text-sm text-gray-600">{label}</h1>

            {/* Visible in normal mode, hidden in print mode */}
            <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 print:hidden">
              <p className="more-lines">{fileName}</p>
              <ToolTip ToolTipItem={fileName || ''} top="top-0" />
              <Image
                src="file-eye"
                className="cursor-pointer"
                objectFit="contain"
                onClick={() => window.open(fileUrl, "_blank")}
                testId={`${key.replace(/_/g, "-")}-image`}
              />
            </div>

            {/* Visible only in print mode */}
            <div className="hidden mt-2 print:block ">
              {isImage ? (
                <img
                  src={fileUrl}
                  alt={fileName}
                  className="h-[200px] w-[200px] object-contain"
                />
              // ) : isPDF ? (
                // <PDFPrinter pdfUrl={fileUrl} />
              ) : (
                <p className="text-sm text-gray-600">Unsupported file type</p>
              )}
            </div>
          </div>
        );
      }
      return (
        <div key={key} className="grid-item">
          <h1 className="text-sm text-gray-600">{label}</h1>
          {(label === 'Valid From' || label === 'Valid Up To')
            ? <div className="group relative">
              <p className="mt-1 font-medium text-gray-900" data-testid={key.replace(/_/g, '-')}>{formatDate(details?.[key])}</p>
              <ToolTip ToolTipItem={formatDate(details?.[key])} />
            </div>
            : <div className="group relative">
              <p className="mt-1 truncate font-medium text-gray-900 " data-testid={key.replace(/_/g, '-')}>{details?.[key] ? details?.[key] : key === '' ? '' : "-"}</p>
              {details?.[key] && <ToolTip ToolTipItem={details?.[key]} />}
            </div>
          }
        </div>
      );
    });
  };


  const passportFields = [
    { key: 'passport_number', label: 'Passport Number' },
    { key: 'passport_valid_from', label: 'Valid From' },
    { key: 'passport_valid_to', label: 'Valid Up To' },
    { key: 'passport_front_page', label: 'Passport Front Page' },
  ];

  const nationalIdFields = [
    { key: 'national_id_number', label: 'National ID Number' },
    { key: 'national_id_valid_from', label: 'Valid From' },
    { key: 'national_id_valid_to', label: 'Valid Up To' },
    { key: 'mothers_name', label: `Mother's Name` },
    { key: '', label: '' },
    { key: 'national_id_front_page', label: 'National ID Front Page' },
    { key: 'national_id_back_page', label: 'National ID Back Page' },
  ];

  interface Wallet {
    wallet_type: string;
    user_id: string;
    balance: number;
    currency: string;
  }

  export default function ViewMasterDistributor({ type, testing, privilege }: UserManagementProps) {
    const { getAPI, PatchAPI } = useApi();
    const { translate, notEnglish, replace } = useTranslation(adminContents);
    const [selectedAction, setSelectedAction] = useState("");
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [isOpen, setIsOpen] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const navigate = useNavigate();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const hasActivateDeactivateMDPrivilege = usePrivilege("MDM6");
    const hasCompanyPrivilege = usePrivilege("CMGT4") || usePrivilege("CMGT3") || usePrivilege("CMGT2") || testing;
    const hasEditMDPrivilege = usePrivilege("MDM5");
    const hasTransactionDetailsPrivilege = usePrivilege(privilege || "MDM7");
    const divRef = useRef<HTMLDivElement | null>(null);
    const [stockDetailsLoading, setStockDetailsLoading] = useState(false);
    const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
    const [stockDetails, setStockDetails] = useState<Wallet>({
      wallet_type: "",
      user_id: "",
      balance: 0,
      currency: "",
    });


    /* The below code is a React useEffect hook that adds an event listener to the document for the "click"
    event. When a click event occurs, it checks if the click target is outside of the element referenced
    by detailsRef. If the click target is outside, it sets the open property of the detailsRef element
    to false. This code is used to handle click events outside of a specific element, in this case, the
    detailsRef element. The useEffect hook ensures that the event listener is added and removed
    appropriately when the component mounts and unmounts. */
    useEffect(() => {
      dispatch(setUserUrl(window.location.pathname));

      const handleClickOutside = (event: MouseEvent) => {
        if (
          detailsRef.current &&
          !detailsRef.current.contains(event.target as Node)
        ) {
          detailsRef.current.open = false;
        }
      };

      document.addEventListener("click", handleClickOutside);

      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, []);

    //function to close the modal
    const closeModal = () => {
      if (
        detailsRef.current
      ) {
        detailsRef.current.open = false;
      }
    }

    const [masterDistributorDetails, setMasterDistributor] = useState<MasterDistributor>({
      id: '',
      user_code: '',
      ussd_code: '',
      user_role: '',
      customer_type: '',
      basic_details: {
        first_name: '',
        last_name: null,
        date_of_birth: null,
        gender: '',
        nationality: '',
        profession: '',
        country_code: '',
        phone_number: '',
        email_address: '',
        secondary_phone_numbers: null,
        country: '',
        city_town: null,
        house_no: null,
        market_block_no: null,
        floor_no: null,
        street_name: null,
        province: null,
        region: null,
        district: null,
        postal_code: '',
        location_name: null,
        latitude: null,
        longitude: null,
        recovery_email_address: '',
        business_assigned_area: {
          region: '',
          provinces: [],
        },
        wallet_access: null,
        onboarded_by: null,
      },
      status: null,
      encrypted_pin: null,
      photo: null,
      user_activity: null,
      profile: null,
      last_logged_in: null,
      identification_details: {
        identification_type: '',
        document_details: null,
      },
      bank_details: null,
      corporate_details: {
        company_id: "",
        company_name: "",
        business_type: "",
        contact_person_name: "",
        company_license_back: "",
        company_license_front: "",
        company_license_number: "",
        employee_id_card_number: "",
        representative_profession: "",
        company_license_expiry_date: "",
        employee_id_card_front_page: ""
      },
      business_details: null,
      service_fee_type: null,
      upfront_service_fee_percentage: null,
      extras: null,
      created_at: '',
      updated_at: '',
    });

    const basicDetailsHeadings = [
      "User Role",
      "Email Address",
      "Phone Number",
      "Date of Birth",
      "Gender",
      "Nationality",
      "Profession",
      "Wallet Type",
      "Onboarded By",
      ...((type === "Customers" && masterDistributorDetails?.customer_type === 'Corporate') ? ["Recovery Email Address"] : []),
      "Secondary Phone Numbers",
    ];

    /**
     * This function fetches details of a master distributor from an API and updates the state accordingly,
     * handling errors and navigation based on the response.
     */
    const fetchmasterDistributorDetails = async () => {
      setLoading(true);
      try {
        const response = await getAPI(`${type.toLowerCase().replace(/ /g, '-')}/${id}`);
        if (!response?.error) {
          const temp = response?.data?.data;
          delete temp["created_at"];
          delete temp["updated_at"];
          setMasterDistributor(temp);
          dispatch(setUserId(id ? id : ""));
          dispatch(setUserCode(temp?.user_code ? temp?.user_code : ""));
          dispatch(setUserName(temp?.basic_details?.first_name ? temp?.basic_details?.first_name + ' ' + temp?.basic_details?.last_name : ""));
          const cvmToDate = new Date(response?.data?.data?.cvm?.to_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
          
          if (cvmToDate && cvmToDate >= today) {
            dispatch(setCompleteCVM({
              from_date: response?.data?.data?.cvm?.from_date,
              to_date: response?.data?.data?.cvm?.to_date,
              targets: response?.data?.data?.cvm?.targets
            }));
            dispatch(setToDate(null));
          } else {
            dispatch(resetCVM());
            dispatch(setToDate(response?.data?.data?.cvm?.to_date));
          }
        } else {
          navigate("/not-found", {
            state: { route: "/admin-management/list-of-admins" },
          });
        }
      } catch (error) {
        console.error("Error fetching admin details:", error);
      }
      setLoading(false);
    };

    useLayoutEffect(() => {
      fetchmasterDistributorDetails();
    }, []);

    const cvmToDate = new Date(masterDistributorDetails?.cvm?.to_date || "");
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

    // const resetOptions = ["Revert Stock", "Add CVM", "Reset PIN", "Print"];
    const resetOptions = type === "Customers" ? ["Revert Stock", "Reset PIN", "Reset Password"] : ["Revert Stock", `${cvmToDate > today ? 'Update CVM' : 'Add CVM'}`, "Reset PIN", "Reset Password"];
    //for printing the user profile
    // const handlePrint = () => {
    //   if (!divRef.current) return;

    //   const printWindow = window.open("", "_blank");

    //   if (!printWindow) {
    //     alert("Popup blocked! Please allow popups for this site.");
    //     return;
    //   }

    //   console.log('divRef.current', divRef.current.outerHTML);
    //   printWindow.document.write(` 
    //     <html>
    //       <head>
    //         <title>Print</title>
    //         <!-- Include Tailwind CSS CDN -->
    //         <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">
    //         <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
    //         <script src="https://cdn.tailwindcss.com"></script>
    //         <style>
    //           body {
    //             font-family: 'Montserrat', 'sans-serif';
    //           }

    //           .text-heading2 {
    //               font-size: 24px;
    //               line-height: 32px;
    //               font-weight: 600;
    //           }

    //           @media print {
    //             body { margin: 0; padding: 0; }
    //           }
    //         </style>
    //       </head>
    //       <body class="p-5">
    //         ${divRef.current.outerHTML} <!-- Inject Printable Area -->
    //       </body>
    //     </html>
    //   `);

    //   printWindow.document.close();

    //   // Wait for styles to apply before triggering print
    //   setTimeout(() => {
    //     printWindow.focus();
    //     printWindow.print();
    //     printWindow.close();
    //   }, 100); // Adjust delay if needed
    // };




    /**
     * The function `handelActions` takes an option as input and performs different actions based on the
     * option selected, such as closing a modal, handling printing, and setting selected action and open
     * state for 'Reset PIN'.
     * @param {string} option - The `option` parameter in the `handelActions` function represents the
     * action that needs to be performed. The function uses a switch statement to determine which action to
     * take based on the value of the `option` parameter. If the `option` is 'Revert Stock' or 'Add C
     */
    const handelActions = (option: string) => {
      switch (option) {
        case 'Revert Stock':
          setSelectedAction(option);
          fetchStock();
          break;
        case 'Update CVM':
        case 'Add CVM':
          navigate(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/add-cvm`)
          break;
        case 'activate':
          setSelectedAction(option);
          setIsOpen(true);
          // navigate(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/childs`)  
          break;
        case 'deactivate':
          setSelectedAction(option); 
          setIsOpen(true);
          // navigate(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/childs`)
          break;
        // case 'Print':
        //   closeModal();
        //   handlePrint();
        //   break;
      }
      if (option === 'Reset PIN' || option === 'Reset Password') {
        setSelectedAction(option);
        setIsOpen(true);
      }

    };
    //function for reverting stock button(this will fetch the stocks of the user)
    const fetchStock = async () => {
      setStockDetailsLoading(true)
      if (masterDistributorDetails?.profile !== 'Approved') {
        dispatch(
          showToast({
            type: "info",
            message: translate(`Wallet will be available only after the profile is approved`),
          })
        );
        setStockDetailsLoading(false)
        return;
      }
      const response = await getAPI(`wallets/etopup/${id}`);
      if (!response?.error) {
        if (response?.data?.data?.wallets?.length === 0 || response?.data?.data?.wallets?.find((wallet: any) => wallet.type === 'etopup')?.balance === 0) {
          dispatch(
            showToast({
              type: "error",
              message: translate(`Stocks are not available to revert`),
            })
          );
          setStockDetailsLoading(false)
          return;
        }
        setStockDetails(response?.data?.data.wallets?.find((wallet: any) => wallet.type === 'etopup'));
        setIsOpen(true);
      } else {
        dispatch(
          showToast({
            type: "error",
            message: response?.data?.data?.message,
          })
        );
      }
      setStockDetailsLoading(false)
    }
    //for reverting the stocks of the user
    const handleRevertStock = async () => {
      setButtonLoading(true);
      const response = await PatchAPI(`${type.toLowerCase().replace(/ /g, '-')}/revert-stock/${id}`);
      if (!response?.error) {
        dispatch(
          showToast({
            type: "success",
            message: translate(`Stock reverted successfully`),
          })
        );
      } else {
        dispatch(
          showToast({
            type: "error",
            message: response?.data?.response?.data?.message,
          })
        );
      }
      setButtonLoading(false);
      setIsOpen(false);
    }
    /**
     * The `handleStatus` function in TypeScript React handles different actions like activating,
     * deactivating, or resetting PIN for a master distributor and displays success or error messages
     * accordingly.
     * @param {string} [text] - The `text` parameter in the `handleStatus` function is a string that
     * represents the deactivate reason for a master distributor when the selected action is "deactivate".
     * If no reason is provided, it defaults to an empty string.
     */
    const handleStatus = async (text?: string) => {
      setButtonLoading(true);

      try {
        let payload: { status?: boolean; deactivate_reason?: string } = {}
        let endPoint = `${type.toLowerCase().replace(/ /g, '-')}/${id}`;
        let message = ''
        switch (selectedAction) {
          case "activate":
            endPoint = `merchants/status/${id}`;
            payload = { status: true };
            message = translate(`${type.slice(0, -1)} activated successfully`)
            toast.success(message);
            break;
          case "deactivate":
            endPoint = `merchants/status/${id}`;
            payload = { status: false, deactivate_reason: text || "" };
            message = translate(`${type.slice(0, -1)} deactivated successfully`)
            toast.success(message);
            break;
          case 'Reset PIN':
            endPoint = `${type.toLowerCase().replace(/ /g, '-')}/reset-pin/${id}`;
            payload = {}
            message = `New PIN has been sent to ${masterDistributorDetails?.basic_details?.first_name}${masterDistributorDetails?.basic_details?.last_name ? ` ${masterDistributorDetails?.basic_details?.last_name}` : ''} (${masterDistributorDetails.user_code}) via ${masterDistributorDetails?.basic_details.email_address ? 'email' : 'phone number'} successfully.`
            toast.success(message);
            break;
          case 'Reset Password':
            endPoint = `${type.toLowerCase().replace(/ /g, '-')}/reset-password/${id}`;
            payload = {}
            message = `New password has been sent to ${masterDistributorDetails?.basic_details?.first_name}${masterDistributorDetails?.basic_details?.last_name ? ` ${masterDistributorDetails?.basic_details?.last_name}` : ''} (${masterDistributorDetails.user_code}) via ${masterDistributorDetails?.basic_details.email_address ? 'email' : 'phone number'} successfully.`
            toast.success(message);
            break;
          case "Revert Stock":
            handleRevertStock();
            toast.success("Stock reverted successfully");
            return;
          default:
            break;
        }

        const res = await PatchAPI(endPoint, payload);

        if (!res.error) {
          dispatch(
            showToast({
              type: "success",
              message: message,
            })
          );
          fetchmasterDistributorDetails();
        } else {
          dispatch(
            showToast({
              type: "error",
              message: res.data.response.data.message,
            })
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (selectedAction !== 'Revert Stock') {
          setIsOpen(false);
          setButtonLoading(false);
        }

      }
    };

    //this function is for the getting the contents for the modal
    const getModelText = () => {
      switch (selectedAction) {
        case 'Revert Stock':
          return {
            title: translate("Revert Stock"),
            description: translate(
              `This action will revert the stock amount of ${stockDetails?.currency || 'AFA'} ${Number.isInteger(stockDetails?.balance) ? stockDetails?.balance + '.00' : stockDetails?.balance || ''} from ${name} (${masterDistributorDetails?.user_code || ''})`),
            buttonName: translate("Revert"),
            navigateTo: () => setIsOpen(false),
            closeModal: () => setIsOpen(false),
            secondaryButtonName: translate("Cancel")
          };
        case "activate":
          return {
            title: translate("Confirm Activation"),
            description: translate(
              "This action will grant access to features and tasks"
            ),
            buttonName: type !== 'Customers' && type !== 'Retailers' ? translate("Just This User") : translate("Activate"),
            secondaryButtonName: type !== 'Customers' && type !== 'Retailers' ? translate("With All Child Users") : translate("Cancel"),
            navigateTo: `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/activate-childs`,
            closeModal: () => setIsOpen(false)
          };
        case "deactivate":
          return {
            title: translate("Confirm Deactivation"),
            description: translate(
              "This action will revoke access to all features and tasks"
            ),
            buttonName: translate("Deactivate"),
            navigateTo: `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/deactivate-childs`,
            secondaryButtonName: type !== 'Customers' && type !== 'Retailers' ? translate("With All Child Users") : translate("Cancel"),
            closeModal: () => setIsOpen(false)
          };
        case "password":
        case "Reset Password":
          return {
            title: translate("Reset Password"),
            description: replace(
              `Would you like to proceed with resetting the password for {{admin_name}} ({{admin_user_code}})?`,
              {
                admin_name: `${masterDistributorDetails?.basic_details?.first_name}${masterDistributorDetails?.basic_details?.last_name ? ` ${masterDistributorDetails?.basic_details?.last_name}` : ''}`,
                admin_user_code: masterDistributorDetails.user_code || '',
              }
            ),
            buttonName: translate("Reset"),
            closeModal: () => setIsOpen(false),
            secondaryButtonName: translate("Cancel")
          };
        case "Reset PIN":
          return {
            title: translate("Reset PIN"),
            description: replace(
              `Would you like to proceed with resetting the PIN for {{admin_name}} ({{admin_user_code}})?`,
              {
                admin_name: `${masterDistributorDetails?.basic_details?.first_name}${masterDistributorDetails?.basic_details?.last_name ? ` ${masterDistributorDetails?.basic_details?.last_name}` : ''}`,
                admin_user_code: masterDistributorDetails.user_code || '',
              }
            ),
            buttonName: translate("Reset"),
            closeModal: () => setIsOpen(false),
            secondaryButtonName: translate("Cancel")
          };
        default:
          return {};
      }
    };


    const name = masterDistributorDetails.basic_details.first_name + ' ' + masterDistributorDetails.basic_details.last_name;
    const addressParts = [
      masterDistributorDetails?.basic_details?.house_no,
      masterDistributorDetails?.basic_details?.street_name,
      masterDistributorDetails?.basic_details?.market_block_no,
      masterDistributorDetails?.basic_details?.floor_no,
      masterDistributorDetails?.basic_details?.district,
      masterDistributorDetails?.basic_details?.city_town,
      masterDistributorDetails?.basic_details?.region,
      masterDistributorDetails?.basic_details?.country,
      masterDistributorDetails?.basic_details?.postal_code
    ];

    const addressString = addressParts
      .filter(part => part) // Remove null, undefined, and empty values
      .join(", "); // Join with comma only for valid values
    const getProfileBgColor = (status: any) => {
      if (status === "Approved") {
        return "bg-[#E8F7ED]";
      } else if (status === "In Review") {
        return "bg-[#E5F0FC]";
      } else if (status === "Pending" || status === "In Progress") {
        return "bg-[#FFEFD9]";
      } else if (status === "Rejected") {
        return "bg-[#FFE5E5]";
      } else {
        return "";
      }
    }
    /**
     * The function `getProfileTextColor` returns a specific text color based on the input status value
     * in a TypeScript React application.
     * @param {any} status - The `getProfileTextColor` function takes a `status` parameter as input and
     * returns a corresponding text color based on the status value. The possible status values and their
     * corresponding text colors are as follows:
     * @returns The function `getProfileTextColor` returns a string representing the color code based on
     * the input `status`. The color code returned depends on the value of `status`:
     */
    const getProfileTextColor = (status: any) => {
      if (status === "Approved") {
        return "text-[#22B14C]";
      } else if (status === "In Review") {
        return "text-[#0072E4]";
      } else if (status === "Pending" || status === "In Progress") {
        return "text-[#FD9101]";
      } else if (status === "Rejected") {
        return "text-[#FF0000]";
      } else {
        return "";
      }
    }

    //function to check the type of the submitted file
    const isImageUrl = (url: string): boolean => {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    };
    return (
      <div dir={notEnglish ? "rtl" : "ltr"}>
        <Breadcrumbs
          navPath={["User Management", `${type} Management`, `List of ${type}`, `View ${type?.slice(0, -1)} Profile`]}
          links={pathname ? [pathname, pathname, pathname] : [`/user-management/${type?.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, `/user-management/${type?.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, `/user-management/${type?.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`]}
          heading={`View ${type?.slice(0, -1)} Profile`}
        />

        {!loading ? (
          <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col" ref={divRef}>
            <div className="px-6 py-5 border-b border-[#E5E9EB] flex justify-between items-center">
              <div>
                <div title={name} data-testid="name" className="flex flex-wrap items-start gap-4 text-heading2">
                  <div data-testid="user-name" className="break-all max-w-[500px]">{name || "-"}</div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-lg shrink-0 ${getProfileBgColor(masterDistributorDetails?.profile)}`}
                  >
                    <h1 data-testid="user-status" className={`text-sm font-medium ${getProfileTextColor(masterDistributorDetails?.profile)}`}>
                      {masterDistributorDetails?.profile}
                    </h1>
                  </div>
                </div>
                <p title={masterDistributorDetails?.user_code} data-testid="user-code" className="text-subTitle">
                  {masterDistributorDetails?.user_code || "-"} | {masterDistributorDetails?.ussd_code || "-"}
                </p>
              </div>
              <div className="flex gap-[24px] print:hidden">
                <Button
                  buttonName={'View Transactions'}
                  className={'bg-white border-[1px] border-blue-800 h-fit px-4 py-2'}
                  textSize="text-large"
                  textColor="text-blue-800"
                  buttonOnclick={() =>
                    navigate(`/user-management/${type?.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type?.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${masterDistributorDetails?.id}/transactions`)
                  }
                // testId="status-btn"
                buttonDisabled={!hasTransactionDetailsPrivilege}
                />
                <Button
                  buttonName={translate(
                    !masterDistributorDetails?.status ? "Activate" : "Deactivate"
                  )}
                  buttonOnclick={() => handelActions(!masterDistributorDetails?.status ? "activate" : "deactivate")}
                  className={`${!masterDistributorDetails?.status
                    ? "bg-positive w-[91px]"
                    : "bg-negative  w-[110px]"
                    } py-2 h-fit px-4 `}
                  bgColor={!masterDistributorDetails?.status ? "bg-positive" : "bg-negative"}
                  textSize="text-large"
                  testId="status-btn"
                  buttonDisabled={!hasActivateDeactivateMDPrivilege && !testing}
                />
                <Button
                  buttonName={translate("Update")}
                  className="py-2 h-fit px-4 w-[91px]"
                  textSize="text-large"
                  buttonOnclick={() =>
                    navigate(
                      `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/edit-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}`
                    )
                  }
                  testId="update-btn"
                  buttonDisabled={!hasEditMDPrivilege && !testing}
                />
                <details ref={detailsRef} className="relative">
                  <summary className="px-2 py-2 bg-primary-500 text-white rounded-full font-semibold cursor-pointer w-[40px] h-[40px] flex items-center justify-center">
                    <Image src="more-vertical" objectFit={"object-contain"} testId="action-btn" />
                  </summary>
                  <div className="absolute z-10 bg-white shadow rounded-md w-[170px] mt-1 right-0">
                    <ul className="p-2 border border-s_dark_hover rounded-lg">
                      {resetOptions.map((item) => (
                        item === "Revert Stock" && stockDetailsLoading ? <RoundedLoader /> :
                          <li
                            onClick={() => handelActions(item)}
                            data-testid={item}
                            key={item} className="text-primary_text hover:text-p_primary cursor-pointer p-2.5 hover:rounded-lg hover:bg-neutral-100 rounded-xs capitalize">
                            {translate(`${item}`)}
                          </li>))}
                    </ul>
                  </div>
                </details>
              </div>
            </div>
            <div
              className={`flex-grow px-6 pb-6 my-5 ${getFirefoxCssFix(
                "scroll"
              )} overflow-auto`}
            >
              <div className="border-b-2 border-gray-200 pb-6">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Basic Details
                </h1>

                <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {basicDetailsHeadings.map((heading, index) => (
                    <div key={index} className="mb-4">
                      <h1 className="text-sm text-gray-600">{heading}</h1>
                      <p data-testid={`${heading.replace(/ /g, '-').toLowerCase()}`} className="mt-1 font-medium text-gray-900 break-all">
                        {(() => {
                          switch (heading) {
                            case "User Role":
                              return type === "Customers" ? `${masterDistributorDetails?.user_role} (${masterDistributorDetails?.customer_type})` : masterDistributorDetails?.user_role || '-';
                            case "Email Address":
                              return masterDistributorDetails?.basic_details.email_address || '-';
                            case "Phone Number":
                              return ((masterDistributorDetails?.basic_details?.country_code || '') + ' ' + (masterDistributorDetails?.basic_details.phone_number || '')) || '-';
                            case "Date of Birth":
                              return formatDate(masterDistributorDetails?.basic_details.date_of_birth ? masterDistributorDetails?.basic_details.date_of_birth : "") || '-';
                            case "Gender":
                              return masterDistributorDetails?.basic_details.gender || '-';
                            case "Nationality":
                              return masterDistributorDetails?.basic_details.nationality || '-';
                            case "Profession":
                              return masterDistributorDetails?.basic_details.profession || '-';
                            case "Wallet Type":
                              return masterDistributorDetails?.basic_details.wallet_access?.map(wallet => wallet?.replace(/_/g, ' ')).join(" | ") || "-";
                            case "Onboarded By":
                              return masterDistributorDetails?.basic_details.onboarded_by || "-";
                            case "Recovery Email Address":
                              return masterDistributorDetails?.basic_details?.recovery_email_address || "-";
                            case "Secondary Phone Numbers":
                              return masterDistributorDetails?.basic_details.secondary_phone_numbers?.length
                                ? masterDistributorDetails.basic_details.secondary_phone_numbers.map(
                                  ({ country_code, phone_number }, index) => (
                                    <div key={`${country_code}-${phone_number}`} data-testid={`phone-number-${index}`}>
                                      {country_code} {phone_number}
                                    </div>
                                  )
                                )
                                : "-";
                            default:
                              return "-";
                          }
                        })()}
                      </p>
                    </div>
                  ))}
                </div>
                <div>
                  <div>
                    <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">Residential Details</h1>
                    <div className="mt-6">
                      <h1 className="text-sm text-gray-600">Address</h1>
                      <p className="mt-1 font-medium text-gray-900 w-[30%]" data-testid="address">{addressString}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">Business Assigned Area</h1>

                    <div>
                      {masterDistributorDetails?.basic_details?.business_assigned_area ? <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <h2 className="text-sm text-gray-600">Region</h2>
                          <p className="mt-1 font-medium text-gray-900" data-testid="region">{masterDistributorDetails?.basic_details?.business_assigned_area?.region || '-'}</p>
                        </div>
                        <div>
                          <h2 className="text-sm text-gray-600">Province (State)</h2>
                          <p className="mt-1 font-medium text-gray-900" data-testid="province">{masterDistributorDetails?.basic_details?.business_assigned_area?.provinces?.join(', ') || '-'}</p>
                        </div>
                      </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                    </div>

                  </div>
                </div>
              </div>
              <div className="mt-6 border-b-2 border-gray-200 pb-6">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                  Identification Details
                </h1>
                {masterDistributorDetails?.identification_details?.identification_type ? <div>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <h1 className="text-sm text-gray-600">Choosen ID</h1>
                      <p className="mt-1 font-medium text-gray-900 break-all" data-testid="identification-type">
                        {masterDistributorDetails?.identification_details?.identification_type}
                      </p>
                    </div>
                    {
                      masterDistributorDetails?.identification_details?.identification_type === "Passport" &&
                      renderFields(
                        masterDistributorDetails?.identification_details?.document_details as Passport,
                        passportFields
                      )
                    }
                    {masterDistributorDetails?.identification_details?.identification_type === 'National ID' &&
                      renderFields(
                        masterDistributorDetails?.identification_details?.document_details as NationalID,
                        nationalIdFields
                      )}
                  </div>
                </div> : <p className="mt-3 font-medium text-gray-900">-</p>}
              </div>
              <div className="mt-6 border-b-2 border-gray-200 pb-4">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">Bank Details</h1>
                <div>
                  {(masterDistributorDetails?.bank_details && masterDistributorDetails?.bank_details.length > 0) ? masterDistributorDetails?.bank_details?.map((bank, index) => (
                    <div className="mt-6 flex flex-col gap-5">
                      <div>
                        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <h1 className="text-sm text-gray-600">Bank Name</h1>
                            <p className="mt-1 font-medium text-gray-900 break-all">{bank.bank_name || '-'}</p>
                          </div>
                          <div>
                            <h1 className="text-sm text-gray-600">Account Number</h1>
                            <p className="mt-1 font-medium text-gray-900 break-all">{bank.account_number || '-'}</p>
                          </div>

                        </div>
                        <div className="mt-4 mb-4 relative group w-1/3">
                          <h1 className="text-sm text-gray-600">Bank Passbook Front Page</h1>
                          {bank?.bank_passbook_front_page ? <div
                            className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 "
                          >
                            <p className="more-lines">{bank?.bank_passbook_front_page.split("/").pop()}</p>
                            <ToolTip ToolTipItem={bank?.bank_passbook_front_page.split("/").pop() || ''} top="top-0" />
                            <Image
                              src="file-eye"
                              className="cursor-pointer "
                              objectFit={'contain'}
                              onClick={() => {
                                const fileUrl = config?.cdnURL + 'public/' + bank?.bank_passbook_front_page;

                                const link = document.createElement('a');
                                link.href = fileUrl;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                link.click();
                              }}
                            />
                          </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                          <div className="hidden mt-2 print:block ">
                            {isImageUrl(config?.cdnURL + 'public/' + bank?.bank_passbook_front_page) && (
                              <img
                                src={config?.cdnURL + 'public/' + bank?.bank_passbook_front_page}
                                alt={config?.cdnURL + 'public/' + bank?.bank_passbook_front_page}
                                className="h-[200px] w-[200px] object-contain"
                              />
                            // ) : (
                            //   bank?.bank_passbook_front_page && <PDFPrinter pdfUrl={config?.cdnURL + 'public/' + bank?.bank_passbook_front_page} />
                            )
                            }
                          </div>

                        </div>
                      </div>
                    </div>
                  )) : <p className="mt-3 font-medium text-gray-900">-</p>}
                </div>
              </div>
              {type !== 'Customers' && <div className="mt-6 border-b-2 border-gray-200 pb-4">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">Business Details</h1>
                {masterDistributorDetails?.business_details ? <><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Store Name</h1>
                    <p className="mt-1 font-medium text-gray-900 break-all">{masterDistributorDetails?.business_details?.store_name || '-'}</p>
                  </div>
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Business Type</h1>
                    <p className="mt-1 font-medium text-gray-900 break-all">{masterDistributorDetails?.business_details?.business_type || '-'}</p>
                  </div>
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Business License Number</h1>
                    <p className="mt-1 font-medium text-gray-900 break-all">{masterDistributorDetails?.business_details?.business_license_number || '-'}</p>
                  </div>
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Business License Valid From</h1>
                    <p className="mt-1 font-medium text-gray-900">{formatDate(masterDistributorDetails?.business_details?.business_valid_from ? masterDistributorDetails?.business_details?.business_valid_from : '') || '-'}</p>
                  </div>
                  <div className="mt-6">
                    <h1 className="text-sm text-gray-600">Business License Valid Up To</h1>
                    <p className="mt-1 font-medium text-gray-900">{formatDate(masterDistributorDetails?.business_details?.business_valid_to ? masterDistributorDetails?.business_details?.business_valid_to : '') || '-'}</p>
                  </div>
                </div><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="mt-4 mb-4 relative group">
                      <h1 className="text-sm text-gray-600">Business License Front Page</h1>
                      {masterDistributorDetails?.business_details?.business_license_front_page ? <div
                        className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                      >
                        <p className="more-lines">{masterDistributorDetails?.business_details?.business_license_front_page?.split("/").pop()}</p>
                        <ToolTip ToolTipItem={masterDistributorDetails?.business_details?.business_license_front_page?.split("/").pop() || ''} top="top-0" />
                        <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                          const fileUrl = config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_front_page;
                          window.open(fileUrl, "_blank");
                        }} />
                      </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                      <div className="hidden mt-2 print:block ">
                        {isImageUrl(config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_front_page) && (
                          <img
                            src={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_front_page}
                            alt={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_front_page}
                            className="h-[200px] w-[200px] object-contain"
                          />
                        // ) : (
                        //   masterDistributorDetails?.business_details?.business_license_front_page && <PDFPrinter pdfUrl={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_front_page} />
                        )
                        }
                      </div>
                    </div>
                    <div className="mt-4 mb-4 relative group">
                      <h1 className="text-sm text-gray-600">Business License Back Page</h1>
                      {masterDistributorDetails?.business_details?.business_license_back_page ? <div
                        className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                      >
                        <p className="more-lines">{masterDistributorDetails?.business_details?.business_license_back_page?.split("/").pop()}</p>
                        <ToolTip ToolTipItem={masterDistributorDetails?.business_details?.business_license_back_page?.split("/").pop() || ''} top="top-0" />
                        <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                          const fileUrl = config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_back_page;
                          window.open(fileUrl, "_blank");
                        }} />
                      </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                      <div className="hidden mt-2 print:block ">
                        {isImageUrl(config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_back_page) && (
                          <img
                            src={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_back_page}
                            alt={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_back_page}
                            className="h-[200px] w-[200px] object-contain"
                          />
                        // ) : (
                        //   masterDistributorDetails?.business_details?.business_license_back_page && <PDFPrinter pdfUrl={config?.cdnURL + 'public/' + masterDistributorDetails?.business_details?.business_license_back_page} />
                        )}
                      </div>

                    </div>
                  </div></> : <p className=" mt-6">-</p>}
              </div>}
              <div className={`mt-6 ${(type === 'Customers' && masterDistributorDetails?.customer_type === 'Individual') ? '' : 'border-b-2 pb-4'} border-gray-200 `}>
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">Photo</h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
                  <div className="mt-6 group relative">
                    <div className=" mb-4">
                      {masterDistributorDetails?.photo ? <div
                        className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                      >
                        <p className="more-lines">{masterDistributorDetails?.photo?.split("/").pop()}</p>
                        <ToolTip ToolTipItem={masterDistributorDetails?.photo?.split("/").pop() || ''} top="top-0" />
                        <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                          const fileUrl = config?.cdnURL + 'public/' + masterDistributorDetails?.photo;
                          window.open(fileUrl, "_blank");
                        }} />
                      </div> : <p className="mt-1 font-medium text-gray-900">-</p>}

                    </div>
                  </div>
                </div>
              </div>
              {/* Service Fee Section */}
              {type !== 'Customers' && <div className="mt-6 border-b-2 border-gray-200 pb-4">
                <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">Service Fee</h1>
                {masterDistributorDetails?.service_fee ? (
                  <div>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <h1 className="text-sm text-gray-600">Parent User</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all">
                          {masterDistributorDetails?.service_fee?.service_fee_parent || '-'}
                        </p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600">Service Fee Type</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all">
                          {masterDistributorDetails?.service_fee?.service_fee_type || '-'}
                        </p>
                      </div>
                      {masterDistributorDetails?.service_fee?.service_fee_type === 'Upfront' && <div>
                        <h1 className="text-sm text-gray-600">Service Fee Percentage</h1>
                        <p className="mt-1 font-medium text-gray-900 break-all">
                          {masterDistributorDetails?.service_fee?.service_fee_type === 'Upfront'
                            ? (masterDistributorDetails?.service_fee?.upfront_service_fee_percentage !== null && masterDistributorDetails?.service_fee?.upfront_service_fee_percentage !== undefined
                              ? `${masterDistributorDetails?.service_fee?.upfront_service_fee_percentage}%`
                              : '-')
                            : '-'}
                        </p>
                      </div>}
                    </div>
                    {masterDistributorDetails?.service_fee?.service_fee_type === 'Postpaid' && masterDistributorDetails?.service_fee?.service_fee_details && (
                      <div className="mt-5">
                        <div className="w-full bg-[#F4F8FF] rounded-lg p-4">
                          <div className="flex text-gray-700 text-sm mb-2">
                            <div className="w-3/12">Telecom</div>
                            <div className="w-3/12">eTop Up Service Fee (%)</div>
                            <div className="w-3/12">Bundle Service Fee (%)</div>
                            <div className="w-2/12">Updated Date</div>
                            <div className="w-1/12">Status</div>
                          </div>
                          {masterDistributorDetails.service_fee.service_fee_details.map((detail, index) => (
                            <div key={index} className="flex text-gray-900 font-medium text-sm py-1 items-center">
                              <div className="w-3/12">{detail.mno_name || '-'}</div>
                              <div className="w-3/12">{detail.etopup_dispersive_fee_percent !== undefined && detail.etopup_dispersive_fee_percent !== null ? detail.etopup_dispersive_fee_percent : '-'}</div>
                              <div className="w-3/12">{detail.bundle_dispersive_fee_percent !== undefined && detail.bundle_dispersive_fee_percent !== null ? detail.bundle_dispersive_fee_percent : '-'}</div>
                              <div className="w-2/12">{detail.updated_at ? moment(detail.updated_at).format('DD-MM-YYYY') : '-'}</div>
                              <div className="w-1/12">{detail.status ? 'Active' : 'Inactive'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 font-medium text-gray-900">-</p>
                )}
              </div>}
              {/* CVM Section */}
              {type !== 'Customers' && <div className="mt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">CVM</h1>
                    {masterDistributorDetails?.cvm?.to_date && (() => {
                      return cvmToDate < today && (
                        <span className="px-2 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-lg">Expired</span>
                      );
                    })()}
                  </div>
                  <Image src={'editImg'} className="cursor-pointer" onClick={() => {
                    navigate(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/add-cvm`)
                  }} />
                </div>
                {masterDistributorDetails?.cvm ? (
                  <div className="mt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div>
                        <h1 className="text-sm text-gray-600">From Date</h1>
                        <p className="mt-1 font-medium text-gray-900">
                          {formatDate(masterDistributorDetails?.cvm?.from_date) || '-'}
                        </p>
                      </div>
                      <div>
                        <h1 className="text-sm text-gray-600">To Date</h1>
                        <p className="mt-1 font-medium text-gray-900">
                          {formatDate(masterDistributorDetails?.cvm?.to_date) || '-'}
                        </p>
                      </div>
                    </div>
                    {masterDistributorDetails?.cvm?.targets && masterDistributorDetails?.cvm?.targets.length > 0 && (
                      <div className="grid grid-row-1 sm:grid-row-2 lg:grid-row-3 gap-4">
                        {masterDistributorDetails?.cvm?.targets.map((target, index) => (
                          <div key={index} className="mb-4 flex w-full items-center">
                            <div className="w-1/3">
                              <h1 className="text-sm text-gray-600">Target</h1>
                              <p className="mt-1 font-medium text-gray-900">{target.target_amount ? 'AFA ' +Number(target.target_amount).toFixed(2) : '-'}</p>
                            </div>
                            <div className="w-1/3">
                              <h1 className="text-sm text-gray-600">On Target Achievement Percentage</h1>
                              <p className="mt-1 font-medium text-gray-900">{target.on_target_achievement_percentage !== undefined && target.on_target_achievement_percentage !== null ? `${Number(target.on_target_achievement_percentage).toFixed(2)}%` : '-'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 font-medium text-gray-900">-</p>
                )}
              </div>}

              {/* Corporate Details Section */}
              {type === 'Customers' && masterDistributorDetails?.customer_type === 'Corporate' && (
                <div className="mt-6">
                  <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400" data-testid="corporate-details-heading">Corporate Details</h1>
                  {masterDistributorDetails?.corporate_details ? (
                    <div>
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="company-name-label">Company Name</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="company-name">{masterDistributorDetails?.corporate_details?.company_name || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="contact-person-name-label">Point of Contact Person Name</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="contact-person-name">{masterDistributorDetails?.corporate_details?.contact_person_name || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="contact-email-label">Point of Contact Person's Email Address</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="contact-email">{masterDistributorDetails?.corporate_details?.contact_email || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="representative-profession-label">Point of Contact Person's Profession</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="representative-profession">{masterDistributorDetails?.corporate_details?.representative_profession || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="company-license-expiry-date-label">Company License Expiry Date</h1>
                          <p className="mt-1 font-medium text-gray-900" data-testid="company-license-expiry-date">{formatDate(masterDistributorDetails?.corporate_details?.company_license_expiry_date) || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="business-type-label">Business Type</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="business-type">{masterDistributorDetails?.corporate_details?.business_type || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="company-license-number-label">Company License Number</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="company-license-number">{masterDistributorDetails?.corporate_details?.company_license_number || '-'}</p>
                        </div>
                        <div>
                          <h1 className="text-sm text-gray-600" data-testid="employee-id-number-label">Employee ID Card Number</h1>
                          <p className="mt-1 font-medium text-gray-900 break-all" data-testid="employee-id-card-number">{masterDistributorDetails?.corporate_details?.employee_id_card_number || '-'}</p>
                        </div>

                      </div>

                      <div className="flex w-full mt-6">
                        <div className="w-1/3 relative group">
                          <h1 className="text-sm text-gray-600" data-testid="license-front-label">Company License Front Page</h1>
                          {masterDistributorDetails?.corporate_details?.company_license_front ? (
                            <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="license-front-container">
                              <p className="more-lines" data-testid="license-front-filename">{masterDistributorDetails?.corporate_details?.company_license_front?.split("/").pop()}</p>
                              <ToolTip ToolTipItem={masterDistributorDetails?.corporate_details?.company_license_front?.split("/").pop() || ''} top="top-0" />
                              {hasCompanyPrivilege && <Image
                                src="file-eye"
                                className="cursor-pointer"
                                objectFit="contain"
                                testId="company-license-front-page-image"
                                onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.company_license_front, "_blank")}
                              />}
                            </div>
                          ) : (
                            <p className="mt-1 font-medium text-gray-900" data-testid="license-front-empty">-</p>
                          )}
                        </div>
                        <div className="w-1/3 group relative">
                          <h1 className="text-sm text-gray-600" data-testid="license-back-label">Company License Back Page</h1>
                          {masterDistributorDetails?.corporate_details?.company_license_back ? (
                            <div className="w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="license-back-container">
                              <p className="more-lines" data-testid="license-back-filename">{masterDistributorDetails?.corporate_details?.company_license_back?.split("/").pop()}</p>
                              <ToolTip ToolTipItem={masterDistributorDetails?.corporate_details?.company_license_back?.split("/").pop() || ''} top="top-0" />
                              {hasCompanyPrivilege && <Image
                                src="file-eye"
                                className="cursor-pointer"
                                objectFit="contain"
                                testId="company-license-back-page-image"
                                onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.company_license_back, "_blank")}
                              />}
                            </div>
                          ) : (
                            <p className="mt-1 font-medium text-gray-900" data-testid="license-back-empty">-</p>
                          )}
                        </div>
                      </div>
                      <div className="w-1/3 group relative mt-6">
                        <h1 className="text-sm text-gray-600" data-testid="employee-id-front-label">Employee ID Card Front Page</h1>
                        {masterDistributorDetails?.corporate_details?.employee_id_card_front_page ? (
                          <div className="w-[68%] flex  justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1" data-testid="employee-id-front-container">
                            <p className="more-lines" data-testid="employee-id-front-filename">{masterDistributorDetails?.corporate_details?.employee_id_card_front_page?.split("/").pop()}</p>
                            <ToolTip ToolTipItem={masterDistributorDetails?.corporate_details?.employee_id_card_front_page?.split("/").pop() || ''} top="top-0" />
                            <Image
                              src="file-eye"
                              className="cursor-pointer"
                              objectFit="contain"
                              testId="employee-id-card-front-page-image"
                              onClick={() => window.open(config?.cdnURL + 'public/' + masterDistributorDetails?.corporate_details?.employee_id_card_front_page, "_blank")}
                            />
                          </div>
                        ) : (
                          <p className="mt-1 font-medium text-gray-900" data-testid="employee-id-front-empty">-</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 font-medium text-gray-900" data-testid="corporate-details-empty">-</p>
                  )}
                </div>
              )}
            </div>
          </div>

        ) : (
          <Shimmer />
        )}
        {isOpen && (
          <CustomModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={getModelText().title}
            subText={getModelText().description}
            primaryButtonText={getModelText().buttonName || ''}
            secondaryButtonText={getModelText().secondaryButtonName || ''}
            handelSubmit={(text) => handleStatus(text)}
            buttonColor={selectedAction === 'deactivate' ? "bg-negative" : selectedAction === 'activate' ? "bg-positive" : 'bg-p_primary'}
            textArea={selectedAction === 'deactivate'}
            buttonLoading={buttonLoading}
            buttonWidth={(selectedAction === 'password' || selectedAction === 'Reset Password' || selectedAction === 'PIN' || selectedAction === 'Revert Stock') && notEnglish ? 'w-fit' : (selectedAction === 'password' || selectedAction === 'Reset Password' || selectedAction === 'PIN' || selectedAction === 'Revert Stock') ? 'w-[90px]' : 'w-[128px]'}
            onHandleClose={(selectedAction === 'activate' || selectedAction === 'deactivate') && (type !== 'Customers' && type !== 'Retailers') ? () => navigate(getModelText().navigateTo || '') : () => getModelText().closeModal?.()}
          />
        )}
      </div>
    );
  }
